import React, { useState } from "react";
import { apiRequest } from "../api/apiClient";

export default function AdminSupport() {
  const [q, setQ] = useState("");
  const [event, setEvent] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [caseNotes, setCaseNotes] = useState([]);
  const [reason, setReason] = useState("");
  const [role, setRole] = useState("admin"); // backend enforces; set from auth context if available
  const [flags, setFlags] = useState([]);
  const [newFlag, setNewFlag] = useState("");
  const [healthCheckStatus, setHealthCheckStatus] = useState("idle"); // idle | running | success | error
  const [healthCheckMessage, setHealthCheckMessage] = useState("");

  const canAct = role === "admin" || role === "auditor";
  const canNote = role === "admin" || role === "auditor";

  const search = async () => {
    try {
      const j = await apiRequest(`/api/admin/users/search?q=${encodeURIComponent(q)}`);
      if (j.success) {
      setUser(j.user);
      await loadTimeline(j.user.publicUserId);
      await loadCaseNotes(j.user.publicUserId);
      await loadFlags(j.user.publicUserId);
      } else alert("User not found");
    } catch (_) {
      alert("User not found");
    }
  };

  const loadTimeline = async (publicUserId) => {
    const params = new URLSearchParams();
    if (event) params.append("event", event);
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    const j = await apiRequest(`/api/admin/support/timeline/${publicUserId}?${params.toString()}`).catch(() => ({}));
    if (j.success) setLogs(j.logs || []);
  };

  const loadCaseNotes = async (publicUserId) => {
    const j = await apiRequest(`/api/admin/support/case-notes/${publicUserId}`).catch(() => ({}));
    if (j.success) setCaseNotes(j.notes || []);
  };

  const loadFlags = async (publicUserId) => {
    const j = await apiRequest(`/api/admin/support/user/${publicUserId}/account-flags`).catch(() => ({}));
    if (j.success) setFlags(j.flags || []);
  };

  const saveFlags = async () => {
    if (!user) return;
    try {
      const j = await apiRequest(`/api/admin/support/user/${user.publicUserId}/account-flags`, {
        method: "POST",
        body: JSON.stringify({ flags }),
      });
      if (!j.success) alert(j.message || "Failed to save flags");
      else alert("Flags saved");
    } catch (e) {
      alert(e?.data?.message || e?.message || "Failed to save flags");
    }
  };

  const action = async (path) => {
    if (!user) return;
    if (!reason || !reason.trim()) return alert("Reason required");
    try {
      const j = await apiRequest(`/api/admin/support/user/${user.publicUserId}/${path}`, {
        method: "POST",
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (!j.success) alert(j.message || "Action failed");
      else {
        alert("Action done");
        setReason("");
        setUser(j.user || user);
      }
    } catch (e) {
      alert(e?.data?.message || e?.message || "Action failed");
    }
  };

  const saveNote = async (id, note) => {
    try {
      const j = await apiRequest(`/api/admin/support/timeline/${id}/add-note`, {
        method: "POST",
        body: JSON.stringify({ note }),
      });
      if (j.success) setLogs((prev) => prev.map((l) => (l._id === id ? j.log : l)));
    } catch (_) {}
  };

  const addCaseNote = async (note) => {
    if (!user || !note || !note.trim()) return;
    try {
      const j = await apiRequest(`/api/admin/support/case-notes/${user.publicUserId}`, {
        method: "POST",
        body: JSON.stringify({ note: note.trim() }),
      });
      if (j.success) setCaseNotes([j.note, ...caseNotes]);
    } catch (_) {}
  };

  const runHealthCheck = async () => {
    setHealthCheckStatus("running");
    setHealthCheckMessage("");
    try {
      let j;
      try {
        j = await apiRequest("/api/admin/ledger/health-check", { method: "POST" });
      } catch (e) {
        j = await apiRequest("/api/health").catch(() => ({}));
      }
      if (j?.ok || j?.success || j?.status === "ok") {
        setHealthCheckStatus("success");
        setHealthCheckMessage("Health check passed");
      } else {
        setHealthCheckStatus("error");
        setHealthCheckMessage(j?.error || j?.message || "Health check failed");
      }
    } catch (err) {
      console.error("Health check error:", err);
      setHealthCheckStatus("error");
      setHealthCheckMessage(err.message || "Health check failed");
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <h2>Admin Support Console</h2>
      <div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Any ID" />
        <button onClick={search}>Search</button>
      </div>

      <div style={{ marginBottom: 10, marginTop: 12 }}>
        <strong>System Health</strong>
        <div style={{ marginTop: 6 }}>
          <button onClick={runHealthCheck} disabled={healthCheckStatus === "running"}>
            Run Health Check
          </button>
          {healthCheckStatus === "running" && <span style={{ marginLeft: 8 }}>Running...</span>}
          {healthCheckStatus === "success" && (
            <span style={{ marginLeft: 8, color: "green" }}>{healthCheckMessage}</span>
          )}
          {healthCheckStatus === "error" && (
            <span style={{ marginLeft: 8, color: "red" }}>{healthCheckMessage}</span>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 10, marginTop: 12 }}>
        <strong>Ledger Controls:</strong>{" "}
        <button
          onClick={async () => {
            try {
              const j = await apiRequest("/api/admin/ledger/health-check", { method: "POST" });
              alert(j.success ? "Health Check OK" : "Health Check FAILED");
              console.log(j);
            } catch (e) {
              alert("Health Check FAILED");
            }
          }}
        >
          Run Ledger Health Check
        </button>{" "}
        <button
          onClick={async () => {
            if (!window.confirm("Are you sure you want to repair unbalanced batches?")) return;
            try {
              const j = await apiRequest("/api/admin/ledger/repair", { method: "POST" });
              alert(j.success ? "Repair Done" : "Repair FAILED");
              console.log(j);
            } catch (e) {
              alert("Repair FAILED");
            }
          }}
        >
          Repair Unbalanced Batches
        </button>
      </div>

      {user && (
        <>
          <h3>
            User: {user.publicUserId} {user.isFrozen ? "(Frozen)" : ""}
          </h3>

          <div style={{ marginBottom: 10 }}>
            <input placeholder="Reason for action" value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: 320 }} />
            <button disabled={!canAct} onClick={() => action("freeze")}>
              Freeze
            </button>
            <button disabled={!canAct} onClick={() => action("unfreeze")}>
              Unfreeze
            </button>
            <button disabled={!canAct} onClick={() => action("reset-kyc")}>
              Reset KYC
            </button>
          </div>

          <h4>Account Flags</h4>
          <div style={{ marginBottom: 10 }}>
            <input placeholder="Add flag (e.g., High Risk)" value={newFlag} onChange={(e) => setNewFlag(e.target.value)} />
            <button
              disabled={!canAct}
              onClick={() => {
                if (!newFlag.trim()) return;
                if (!flags.includes(newFlag.trim())) setFlags([...flags, newFlag.trim()]);
                setNewFlag("");
              }}
            >
              Add
            </button>
            <ul style={{ listStyle: "none", paddingLeft: 0 }}>
              {flags.map((f) => (
                <li key={f}>
                  {f}{" "}
                  <button disabled={!canAct} onClick={() => setFlags(flags.filter((x) => x !== f))}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <button disabled={!canAct} onClick={saveFlags}>
              Save Flags
            </button>
          </div>

          <h4>Timeline</h4>
          <ul style={{ listStyle: "none", paddingLeft: 0 }}>
            {logs.map((l) => (
              <li key={l._id} style={{ marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 8 }}>
                <div>
                  {new Date(l.at).toLocaleString()} — <b>{l.event}</b>
                </div>
                <div>
                  Note:{" "}
                  {canNote ? (
                    <input defaultValue={l.notes || ""} onBlur={(e) => saveNote(l._id, e.target.value)} style={{ width: 280 }} />
                  ) : (
                    <span>{l.notes || "-"}</span>
                  )}
                  <small>
                    {" "}
                    by {l.notesBy || "-"} {l.notesAt ? " @ " + new Date(l.notesAt).toLocaleString() : ""}
                  </small>
                </div>
              </li>
            ))}
          </ul>

          <h4>Case Notes (Internal)</h4>
          <button
            disabled={!canNote}
            onClick={() => {
              const n = prompt("Add case note:");
              if (n) addCaseNote(n);
            }}
          >
            Add Case Note
          </button>
          <ul style={{ listStyle: "none", paddingLeft: 0 }}>
            {caseNotes.map((c) => (
              <li key={c._id} style={{ marginBottom: 4 }}>
                {new Date(c.at).toLocaleString()} — <b>{c.author}</b>: {c.note}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
